/**
 * Disparos de WhatsApp: novo envio em massa para publicos pos-fato, retorno
 * de cada envio, protecao do numero do comercial e os modelos que fazem
 * pagar. Tela da administracao: operamos em nome da empresa escolhida.
 *
 * Hierarquia, de cima para baixo:
 *  1. Identidade (empresa e numero de saida no aside).
 *  2. Retorno dos envios da empresa no periodo, com a variacao contra o
 *     periodo anterior (recuperado, comissao, pagaram, respostas, custo,
 *     custo por recuperacao).
 *  3. "O que fazer agora" (derivado dos dados) ao lado do "Parado agora".
 *  4. Abas: Campanhas (novo envio + envios + opt-out) e Modelos (ranking).
 *
 * Decisoes nao obvias:
 *  - O Select "Empresa" chama `selectCompany`: a empresa em foco do cabecalho
 *    e a empresa deste formulario sao a MESMA coisa. Duas fontes de verdade
 *    aqui seria disparar para a empresa errada.
 *  - TRES recursos: envios de TODAS as empresas (a tabela filtra por empresa
 *    e compara com o periodo anterior sem nova requisicao), o retrato da
 *    empresa (numeros, publicos, vendedoras, saude) e os modelos (nao
 *    dependem de empresa nem periodo). A falha de um nao some com os outros
 *    sem aviso: cada um tem o proprio ErrorPanel.
 *  - Envios locais e ajustes (pausar, retomar) vivem NESTA pagina, chaveados
 *    por id: o progresso simulado continua andando enquanto a pessoa olha a
 *    aba de modelos, e nao se perde ao trocar de empresa e voltar.
 *  - `?segmento=` na URL (vindo do radar do hub) prepara o formulario com o
 *    publico sugerido e sai da URL em seguida, para um F5 nao repetir.
 *  - Toda leitura parte de um fato ja ocorrido. Nao existe publico "toda a
 *    base" nem lembrete antes de vencer: sem o fato nao ha comissao.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, Footnote, HeroBanner, Panel, Select, Tabs, type TabItem } from '../../../components/ui';
import { AGORA_SIMULADO, useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate, maskPhoneDisplay } from '../../../lib/format';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import { fetchBroadcasts, fetchTemplatesSummary, fetchWhatsAppContext } from '../api';
import { desempenhoDosModelos, totaisAnteriores, totalizarEnvios } from '../comparar';
import { AbaCampanhas, type PreparoDeEnvio } from '../components/AbaCampanhas';
import { EsqueletoDisparos } from '../components/EsqueletoDisparos';
import { GradeDeRetorno } from '../components/GradeDeRetorno';
import { ModalRelatorioEnvio } from '../components/ModalRelatorioEnvio';
import { NumerosBloqueados } from '../components/NumerosBloqueados';
import { OQueFazerAgora } from '../components/OQueFazerAgora';
import { ParadoAgora } from '../components/ParadoAgora';
import { ResumoDeModelos } from '../components/ResumoDeModelos';
import { TabelaDeEnvios } from '../components/TabelaDeEnvios';
import { VoltarPara } from '../components/VoltarPara';
import { insightsDoWhatsApp, type AcaoInsight } from '../insights';
import { medidasDeRetorno } from '../medidas';
import { avancarEnvio, envioEmAndamento } from '../progresso';
import { LOTE_DA_TABELA, estadoInicialDaTabela, type EstadoTabelaEnvios } from '../tabelaEnvios';
import type { Broadcast, SegmentKey } from '../types';
import { useColecaoLocal } from '../useColecaoLocal';
import { useFeedbackTemporario } from '../useFeedbackTemporario';
import { useProgressoSimulado } from '../useProgressoSimulado';
import { NUMBER_QUALITY_LABEL, isSegmentKey } from '../vocabulario';

type Aba = 'campanhas' | 'modelos';

const SEM_ENVIOS: readonly Broadcast[] = [];

function rolarAte(id: string): void {
  window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
}

export function DisparosWhatsApp(): JSX.Element {
  const { company, companies, selectCompany } = useCompany();
  const { range, selection } = usePeriod();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `Disparos de WhatsApp — ${company.name} · Converte Fácil`;
  }, [company.name]);

  const buscarEnvios = useCallback((signal: AbortSignal) => fetchBroadcasts(null, range, signal), [range]);
  const buscarContexto = useCallback((signal: AbortSignal) => fetchWhatsAppContext(company.id, signal), [company.id]);
  const buscarModelos = useCallback((signal: AbortSignal) => fetchTemplatesSummary(signal), []);
  const envios = useApiResource(buscarEnvios, 30_000);
  const contexto = useApiResource(buscarContexto, 60_000);
  const modelos = useApiResource(buscarModelos);

  const [aba, setAba] = useState<Aba>('campanhas');
  const [senderId, setSenderId] = useState<string | null>(null);
  const [preparo, setPreparo] = useState<PreparoDeEnvio | null>(null);
  const [relatorioId, setRelatorioId] = useState<string | null>(null);
  const [tabela, setTabela] = useState<EstadoTabelaEnvios>(() => estadoInicialDaTabela(company.id));
  const { mensagem, mostrar } = useFeedbackTemporario();

  // A tabela acompanha a empresa em foco; "todas" continua disponivel no Select.
  // O atalho "Preparar envio" de uma empresa nao vale para a proxima.
  const empresaAnterior = useRef(company.id);
  useEffect(() => {
    setTabela((atual) => ({ ...atual, empresa: company.id, limite: LOTE_DA_TABELA }));
    if (empresaAnterior.current !== company.id) {
      empresaAnterior.current = company.id;
      setPreparo(null);
    }
  }, [company.id]);

  const preparar = useCallback((segmento: SegmentKey): void => {
    setAba('campanhas');
    setPreparo((prev) => ({ versao: (prev?.versao ?? 0) + 1, segmento }));
  }, []);

  // `?segmento=` vindo do hub: prepara o formulario e sai da URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const valor = params.get('segmento');
    if (valor === null) {
      return;
    }
    if (isSegmentKey(valor)) {
      preparar(valor);
    }
    params.delete('segmento');
    const resto = params.toString();
    navigate({ pathname: location.pathname, search: resto === '' ? '' : `?${resto}` }, { replace: true });
  }, [location.search, location.pathname, navigate, preparar]);

  const dadosContexto = contexto.state.status === 'success' ? contexto.state.data : null;
  const senders = dadosContexto?.senders ?? [];
  const sender = senders.find((s) => s.id === senderId && s.connected) ?? senders.find((s) => s.connected) ?? senders[0] ?? null;
  const health = sender === null || !sender.connected || dadosContexto === null ? null : dadosContexto.health.find((h) => h.senderId === sender.id) ?? null;

  const colecao = useColecaoLocal<Broadcast>();
  const base = envios.state.status === 'success' ? envios.state.data.broadcasts : SEM_ENVIOS;
  const aplicarLocais = colecao.aplicar;
  const todosOsEnvios = useMemo(() => aplicarLocais(base), [aplicarLocais, base]);
  const enviosDaEmpresa = useMemo(() => todosOsEnvios.filter((e) => e.companyId === company.id), [todosOsEnvios, company.id]);
  useProgressoSimulado(todosOsEnvios, envioEmAndamento, avancarEnvio, colecao.ajustar);

  const desempenho = useMemo(() => desempenhoDosModelos(todosOsEnvios), [todosOsEnvios]);
  const simulado =
    (envios.state.status === 'success' && envios.state.simulated) || (contexto.state.status === 'success' && contexto.state.simulated) || (modelos.state.status === 'success' && modelos.state.simulated);
  // Relogio da tela: o do mock na previa (datas fixas), o real em producao.
  const agora = useMemo(() => (simulado ? AGORA_SIMULADO : new Date()), [simulado]);

  const dadosEnvios = envios.state.status === 'success' ? envios.state.data : null;
  const temAnterior = dadosEnvios !== null && dadosEnvios.previousPeriod !== null;
  const atual = useMemo(() => totalizarEnvios(enviosDaEmpresa), [enviosDaEmpresa]);
  const anterior = dadosEnvios === null ? null : totaisAnteriores(dadosEnvios.previous, temAnterior, { companyId: company.id, senderKind: null });

  const locais = colecao.locais;
  const oportunidades = useMemo(() => {
    if (dadosEnvios === null) {
      return [];
    }
    return dadosEnvios.opportunities
      .filter((o) => o.companyId === company.id)
      .map((o) => {
        const local = locais.find((e) => e.companyId === o.companyId && e.audience.segments.includes(o.segmentKey));
        return local === undefined ? o : { ...o, lastBroadcastAt: local.createdAt };
      });
  }, [dadosEnvios, company.id, locais]);

  const insights = useMemo(
    () => (dadosContexto === null ? [] : insightsDoWhatsApp({ company, contexto: dadosContexto, envios: enviosDaEmpresa, oportunidades, agora })),
    [company, dadosContexto, enviosDaEmpresa, oportunidades, agora],
  );

  const executar = (acao: AcaoInsight): void => {
    switch (acao.kind) {
      case 'retomar': {
        colecao.ajustar(acao.broadcastId, { status: 'em_andamento', pausedReason: null });
        const alvo = todosOsEnvios.find((e) => e.id === acao.broadcastId);
        mostrar(`"${alvo?.name ?? 'Envio'}" retomado. O progresso segue na lista de envios.`);
        setAba('campanhas');
        rolarAte('envios');
        return;
      }
      case 'preparar':
        if (acao.companyId !== company.id) {
          selectCompany(acao.companyId);
        }
        preparar(acao.segmentKey);
        return;
      case 'ancora':
        setAba('campanhas');
        rolarAte(acao.id);
        return;
      case 'rota':
        navigate({ pathname: acao.rota, search: location.search });
        return;
      case 'abrir_empresa':
        selectCompany(acao.companyId);
        setAba('campanhas');
        rolarAte('envios');
        return;
    }
  };

  const abas: readonly TabItem<Aba>[] = [
    { value: 'campanhas', label: 'Campanhas', icon: 'disparo', count: dadosEnvios === null ? undefined : formatCount(enviosDaEmpresa.length) },
    { value: 'modelos', label: 'Modelos que fazem pagar', icon: 'modelo', count: modelos.state.status === 'success' ? formatCount(modelos.state.data.length) : undefined },
  ];

  const carregando = envios.state.status === 'loading' || contexto.state.status === 'loading' || modelos.state.status === 'loading';
  const relatorio = relatorioId === null ? null : todosOsEnvios.find((e) => e.id === relatorioId) ?? null;
  const periodoTexto = describeSelection(selection);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="whatsapp"
        tone="good"
        backTo={<VoltarPara to="/admin/disparos" label="Disparos" />}
        title="Disparos de WhatsApp"
        badges={
          <>
            <Badge tone="brand" dot>
              Administração
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description={`${company.name} · ${periodoTexto}. Envios em massa pelo WhatsApp do comercial para quem já abandonou, teve o pagamento recusado, venceu ou respondeu sem fechar.`}
        aside={
          <>
            <RefreshingDot active={(envios.state.status === 'success' && envios.state.refreshing) || (contexto.state.status === 'success' && contexto.state.refreshing)} />
            <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-subtle sm:w-auto sm:flex-row sm:items-center sm:gap-2">
              Empresa
              <span className="block w-full sm:w-48">
                <Select size="sm" value={company.id} onChange={(e) => selectCompany(e.target.value)}>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </span>
            </label>
            <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-subtle sm:w-auto sm:flex-row sm:items-center sm:gap-2">
              Número de saída
              <span className="block w-full sm:w-72">
                <Select size="sm" value={sender?.id ?? ''} disabled={senders.length === 0} onChange={(e) => setSenderId(e.target.value)}>
                  {senders.length === 0 ? <option value="">Nenhum número disponível</option> : null}
                  {senders.map((s) => {
                    const h = dadosContexto?.health.find((x) => x.senderId === s.id) ?? null;
                    return (
                      <option key={s.id} value={s.id} disabled={!s.connected}>
                        {s.label} · {maskPhoneDisplay(s.phoneE164)}
                        {s.connected ? (h === null ? '' : ` · ${NUMBER_QUALITY_LABEL[h.quality].toLowerCase()}`) : ' (desconectado)'}
                      </option>
                    );
                  })}
                </Select>
              </span>
            </label>
            <Button
              variant="secondary"
              size="sm"
              icon="atualizar"
              onClick={() => {
                envios.reload();
                contexto.reload();
              }}
            >
              Atualizar
            </Button>
          </>
        }
      />

      {carregando ? <EsqueletoDisparos variante="whatsapp" /> : null}
      {envios.state.status === 'error' ? <ErrorPanel title="Não foi possível carregar os envios de WhatsApp" error={envios.state.error} onRetry={envios.reload} /> : null}
      {contexto.state.status === 'error' ? <ErrorPanel title={`Não foi possível carregar os números e públicos de ${company.name}`} error={contexto.state.error} onRetry={contexto.reload} /> : null}
      {modelos.state.status === 'error' ? <ErrorPanel title="Não foi possível carregar os modelos de mensagem" error={modelos.state.error} onRetry={modelos.reload} /> : null}

      {dadosEnvios !== null && dadosContexto !== null && modelos.state.status === 'success' ? (
        <>
          {simulado ? (
            <SimulatedNotice>
              Envios, públicos, vendedoras e modelos são fictícios e fixos para {company.name}, com tarifas de exemplo. Iniciar, pausar, retomar e agendar funcionam só nesta tela.
            </SimulatedNotice>
          ) : null}

          {sender !== null && sender.connected ? null : (
            <Panel tone="warning" className="px-5 py-4 text-sm text-ink">
              <span className="font-medium">Nenhum número de saída conectado para {company.name}.</span> Conecte o WhatsApp do comercial (Evolution) ou a API oficial no cadastro da empresa antes de
              disparar.
            </Panel>
          )}

          {mensagem === null ? null : (
            <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-ink">
              <Badge tone="warning" dot>
                Simulado na prévia
              </Badge>
              {mensagem}
            </p>
          )}

          <GradeDeRetorno
            id="retorno-whatsapp"
            titulo="Retorno dos envios"
            descricao={`${company.name} no período do cabeçalho, com a variação contra o período anterior de mesmo tamanho.`}
            medidas={medidasDeRetorno(atual, anterior, company.feeBps)}
            aside={
              <Badge tone="neutral" icon="calendario">
                {periodoTexto}
              </Badge>
            }
            rodape={
              <Footnote>
                {dadosEnvios.previousPeriod === null
                  ? 'A janela "desde o início" não tem período anterior para comparar.'
                  : `Comparado com ${formatDate(dadosEnvios.previousPeriod.start)} a ${formatDate(dadosEnvios.previousPeriod.end)}. Comissão pela regra do ADR-001: último contato, 7 dias, valor pago.`}
              </Footnote>
            }
          />

          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerAgora
              className="lg:col-span-2"
              insights={insights}
              onAcao={executar}
              descricao="Sugestões derivadas dos envios, dos públicos parados e da saúde do número. Cada uma diz de onde veio."
              vazio="Nenhum envio pausado, número saudável e nenhum público grande sem envio. O motor segue sozinho depois de cada abandono ou vencimento."
            />
            <ParadoAgora segments={dadosContexto.segments} onPreparar={preparar} />
          </div>

          <Tabs items={abas} value={aba} onChange={setAba} label="Seções dos disparos de WhatsApp" panelId="painel-disparos-whatsapp" />

          <div id="painel-disparos-whatsapp" role="tabpanel" className="flex flex-col gap-6">
            {aba === 'campanhas' ? (
              <>
                <AbaCampanhas
                  key={company.id}
                  company={company}
                  contexto={dadosContexto}
                  templates={modelos.state.data}
                  sender={sender}
                  health={health}
                  desempenho={desempenho}
                  preparo={preparo}
                  onCriar={colecao.adicionar}
                  proximoNumeroLocal={colecao.locais.length + 1}
                />
                <TabelaDeEnvios
                  envios={todosOsEnvios}
                  companies={companies}
                  estado={tabela}
                  onEstado={(patch) => setTabela((t) => ({ ...t, ...patch }))}
                  agora={agora}
                  acoes={{
                    onPausar: (id) => {
                      colecao.ajustar(id, { status: 'pausada', pausedReason: 'manual' });
                      mostrar('Envio pausado. Ninguém mais recebe até você retomar.');
                    },
                    onRetomar: (id, semTrava) => {
                      colecao.ajustar(id, semTrava ? { status: 'em_andamento', pausedReason: null, autoPauseOptOutBps: null } : { status: 'em_andamento', pausedReason: null });
                      mostrar(semTrava ? 'Envio retomado sem a trava de opt-out. Acompanhe o opt-out na lista.' : 'Envio retomado.');
                    },
                    onIniciarAgora: (id) => {
                      colecao.ajustar(id, { status: 'em_andamento', scheduledFor: null, startedAt: new Date().toISOString() });
                      mostrar('Envio agendado iniciado agora.');
                    },
                    onRelatorio: setRelatorioId,
                  }}
                />
                <NumerosBloqueados optOuts={dadosContexto.optOuts} total={dadosContexto.optOutTotal} />
              </>
            ) : (
              <ResumoDeModelos templates={modelos.state.data} desempenho={desempenho} />
            )}
          </div>

          <p className="text-xs text-ink-subtle">
            Período de {formatDate(dadosEnvios.periodStart)} a {formatDate(dadosEnvios.periodEnd)} ({periodoTexto}) · {company.name} · {formatCount(dadosContexto.optOutTotal)} números bloqueados ·
            tarifas {dadosContexto.tariffs.source === 'exemplo' ? 'de exemplo' : 'da fatura'}. Nenhum envio fala com quem ainda não abandonou ou venceu.
          </p>

          <ModalRelatorioEnvio envio={relatorio} segments={dadosContexto.segments} sellers={dadosContexto.sellers} templates={modelos.state.data} agora={agora} onClose={() => setRelatorioId(null)} />
        </>
      ) : null}
    </section>
  );
}
